import { CompanyImportMatchCandidate, NearestModelledParent } from '@upright/common/model/input/companies/import/types';
import { isNil } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { isEnoughAccessToReadWithoutWeights } from '../../../../../../../common/iam/access/types';
import { isProductLike } from '../../../../../../../common/model/input';
import { PossiblyNamedModelEntityReferenceWithAccessLevel } from '../../../../../../../common/model/input/types';
import SmallExternalLinkIcon from '../../../../../generic/components/icons/external-link-icon';
import { SortOrder, toSortComparisonValue } from '../../../../../generic/content/tables/sortable-table';
import { NumberCell } from '../table-styles';

export const CenteredNumberCell = styled(NumberCell)`
  text-align: center;
`;

export function defaultComparator(a: number | undefined | null, b: number | undefined | null, order: SortOrder) {
  const compared = toSortComparisonValue(a, order) - toSortComparisonValue(b, order);
  return compared;
}

export function getTextComparator<T>(getCellValue: (datum: T) => string | undefined) {
  return (a: T, b: T) => (getCellValue(a) || '').localeCompare(getCellValue(b) || '');
}

export function getNumberComparator<T>(getCellValue: (datum: T) => number | undefined) {
  return (a: T, b: T, order: SortOrder) => {
    return toSortComparisonValue(getCellValue(a), order) - toSortComparisonValue(getCellValue(b), order);
  };
}

export function toExportValue(value: number | undefined) {
  return value === 0 ? 0 : value || '';
}

export function noAccessToRowDatum(value: PossiblyNamedModelEntityReferenceWithAccessLevel) {
  if (isProductLike(value.type)) {
    return false;
  }
  // As there is no access information added to legal entities with no parents but we do want to allow
  // navigating to the page of these entities, we should set disabled to false on these rows.
  if (value.id.length > 0 && value.legalEntityId && !value.entityId) {
    return false;
  }
  return !isEnoughAccessToReadWithoutWeights(value.accessLevel);
}

export const DefaultCellSkeleton = () => (
  <SkeletonWrap>
    <Skeleton width={'8em'} />
  </SkeletonWrap>
);

const SkeletonWrap = styled.div`
  padding-left: 0.5em;
  padding-right: 0.5em;
`;

interface SimpleInfoProps {
  attribute: string;
  value: string | number | undefined;
  href?: string;
  truncateTo?: number;
}
export function SimpleInfoRow({ attribute, value, href, truncateTo }: SimpleInfoProps) {
  const valueStr = !isNil(value) ? `${value}` : 'undefined';
  const valueTrunc = valueStr.slice(0, truncateTo);
  return (
    <p>
      <>{attribute}: </>
      <>
        {valueTrunc}
        {valueTrunc.length < valueStr.length && '...'}
      </>
      {href && (
        <a target="_blank" rel="noreferrer" href={href}>
          <SmallExternalLinkIcon />
        </a>
      )}
    </p>
  );
}

export function friendlyQuality(candidate: CompanyImportMatchCandidate | NearestModelledParent | undefined) {
  const text = `${candidate?.productSetQuality || ''}${candidate?.inInvestorLicenseUniverse ? '*' : ''}`;
  if (!text.length) return undefined;
  return text;
}
